import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { finalize, map, tap, catchError, shareReplay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { TokenService, LogService, UtilsService } from 'abp-ng2-module';
import { AppConsts } from '@shared/AppConsts';
import { LocalStorageService } from '@shared/utils/local-storage.service';
import { AppSessionService } from '@shared/session/app-session.service';
import {
    AuthenticateModel,
    AuthenticateResultModel,
    ExternalAuthenticateModel,
    ExternalAuthenticateResultModel,
    TokenAuthServiceProxy,
} from '@shared/service-proxies/service-proxies';

@Injectable()
export class AppAuthService {
    authenticateModel: AuthenticateModel;
    authenticateResult: AuthenticateResultModel;
    rememberMe: boolean;

    // Shared in-flight refresh so concurrent 401s trigger only one refresh call.
    private refreshInFlight$: Observable<string | null> | null = null;

    constructor(
        private _tokenAuthService: TokenAuthServiceProxy,
        private _router: Router,
        private _utilsService: UtilsService,
        private _tokenService: TokenService,
        private _logService: LogService,
        private _localStorageService: LocalStorageService,
        private _sessionService: AppSessionService,
        private _http: HttpClient
    ) {
        this.clear();
    }

    logout(reload?: boolean): void {
        abp.auth.clearToken();
        abp.utils.deleteCookie(AppConsts.authorization.encryptedAuthTokenName);
        this.clearRefreshToken();

        if (reload !== false) {
            location.href = AppConsts.appBaseUrl;
        }
    }

    authenticate(finallyCallback?: () => void): void {
        finallyCallback = finallyCallback || (() => { });

        // Get tenancy name from local storage before authenticating
        this._localStorageService.getItem("tenancyName", (err, tenancyName) => {
            if (!err && tenancyName) {
                this.authenticateModel.tenancyName = tenancyName;
            } else {
                // If no tenancy name found or error, set empty string for host authentication
                this.authenticateModel.tenancyName = '';
            }

            // Proceed with authentication
            this._tokenAuthService
                .authenticate(this.authenticateModel)
                .pipe(
                    finalize(() => {
                        finallyCallback();
                    })
                )
                .subscribe((result: AuthenticateResultModel) => {
                    this.processAuthenticateResult(result);
                });
        });
    }

    private processAuthenticateResult(
        authenticateResult: AuthenticateResultModel
    ) {
        this.authenticateResult = authenticateResult;

        if (authenticateResult.accessToken) {
            // Successfully logged in
            this.setRefreshToken(authenticateResult.refreshToken);
            this.login(
                authenticateResult.accessToken,
                authenticateResult.encryptedAccessToken,
                authenticateResult.expireInSeconds,
                this.rememberMe
            );
        } else {
            // Unexpected result!

            this._logService.warn('Unexpected authenticateResult!');
            this._router.navigate(['account/login']);
        }
    }

    private login(
        accessToken: string,
        encryptedAccessToken: string,
        expireInSeconds: number,
        rememberMe?: boolean
    ): void {
        const tokenExpireDate = rememberMe
            ? new Date(new Date().getTime() + 1000 * expireInSeconds)
            : undefined;

        this._tokenService.setToken(accessToken, tokenExpireDate);

        this._utilsService.setCookieValue(
            AppConsts.authorization.encryptedAuthTokenName,
            encryptedAccessToken,
            tokenExpireDate,
            abp.appPath
        );

        // Initialize session before determining redirect route
        this._sessionService.init().then(() => {
            // Determine redirect route based on user type (tenant vs host)
            const redirectRoute = this.getRedirectRoute();
            location.href = AppConsts.appBaseUrl + redirectRoute;
        }).catch(error => {
            console.error('Failed to initialize session:', error);
            // Fallback to cockpit if session init fails
            location.href = AppConsts.appBaseUrl + '/app/cockpit';
        });
    }

    private clear(): void {
        this.authenticateModel = new AuthenticateModel();
        this.authenticateModel.rememberClient = false;
        this.authenticateModel.tenancyName = '';
        this.authenticateResult = null;
        this.rememberMe = false;
    }

    externalAuthenticate(externalAuthModel: ExternalAuthenticateModel): Observable<ExternalAuthenticateResultModel> {
        return this._tokenAuthService.externalAuthenticate(externalAuthModel);
    }

    setToken(accessToken: string): void {
        this._tokenService.setToken(accessToken);
    }

    private getRefreshToken(): string | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }
        return localStorage.getItem(AppConsts.authorization.refreshTokenName);
    }

    private setRefreshToken(refreshToken: string | undefined): void {
        if (typeof localStorage === 'undefined' || !refreshToken) {
            return;
        }
        localStorage.setItem(AppConsts.authorization.refreshTokenName, refreshToken);
    }

    private clearRefreshToken(): void {
        this.refreshInFlight$ = null;
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(AppConsts.authorization.refreshTokenName);
        }
    }

    /**
     * Exchange the stored refresh token for a fresh access token. Emits the new
     * token (and persists it), or null when there's no refresh token or the call
     * fails. Concurrent callers share a single in-flight request.
     */
    refreshAccessToken(): Observable<string | null> {
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            return of(null);
        }

        this.refreshInFlight$ = this._http
            .post<any>(
                AppConsts.remoteServiceBaseUrl + '/api/TokenAuth/RefreshToken',
                null,
                { params: { refreshToken } }
            )
            .pipe(
                map((response) => response?.result?.accessToken ?? null),
                tap((newToken: string | null) => {
                    if (newToken) {
                        this._tokenService.setToken(newToken);
                    }
                }),
                catchError(() => of(null)),
                finalize(() => {
                    this.refreshInFlight$ = null;
                }),
                shareReplay(1)
            );

        return this.refreshInFlight$;
    }

    /**
     * Handle external authentication result
     */
    handleExternalAuthResult(result: ExternalAuthenticateResultModel): void {
        if (result.accessToken) {
            const tokenExpireDate = new Date(new Date().getTime() + 1000 * result.expireInSeconds);

            this._tokenService.setToken(result.accessToken, tokenExpireDate);
            this.setRefreshToken(result.refreshToken);

            if (result.encryptedAccessToken) {
                this._utilsService.setCookieValue(
                    AppConsts.authorization.encryptedAuthTokenName,
                    result.encryptedAccessToken,
                    tokenExpireDate,
                    abp.appPath
                );
            }

            // Initialize session before determining redirect route
            this._sessionService.init().then(() => {
                // Determine redirect route based on user type (tenant vs host)
                const redirectRoute = this.getRedirectRoute();
                location.href = AppConsts.appBaseUrl + redirectRoute;
            }).catch(error => {
                console.error('Failed to initialize session:', error);
                // Fallback to cockpit if session init fails
                location.href = AppConsts.appBaseUrl + '/app/cockpit';
            });
        }
    }

    /**
     * Determine the appropriate redirect route based on user type
     */
    private getRedirectRoute(): string {
        // Check if user is a tenant (has tenant info)
        const isTenant = this._sessionService.tenant && this._sessionService.tenant.id > 0;

        if (isTenant) {
            return '/app/cockpit';
        } else {
            return '/app/about';
        }
    }
}
